(() => {
  const MIN_QUERY_LENGTH = 1;
  const DEBOUNCE_MS = 220;
  const CLOSE_ANIMATION_MS = 220;

  const escapeHtml = (value) =>
    String(value || '').replace(/[&<>"']/g, (char) => {
      switch (char) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '"':
          return '&quot;';
        case "'":
          return '&#039;';
        default:
          return char;
      }
    });

  const decodeHtml = (value) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value || '';
    return textarea.value;
  };

  const normalizeHref = (href) => {
    const decoded = decodeHtml(href).trim();
    if (!decoded || decoded === '#') return '#';

    try {
      const url = new URL(decoded, window.location.origin);
      return `${url.pathname}${url.search}`;
    } catch (_error) {
      return '#';
    }
  };

  const normalizeImageUrl = (url) => {
    const normalized = decodeHtml(url).trim();
    if (!normalized) return '';
    if (normalized.startsWith('//')) return `${window.location.protocol}${normalized}`;

    try {
      return new URL(normalized, window.location.origin).toString();
    } catch (_error) {
      return '';
    }
  };

  const getPredictiveEndpoint = () => {
    if (window.Theme && window.Theme.routes && window.Theme.routes.predictive_search_url) {
      return window.Theme.routes.predictive_search_url;
    }

    return '/search/suggest';
  };

  const cleanText = (element) => (element?.textContent || '').replace(/\s+/g, ' ').trim();

  const createSearchController = (search) => {
    const input = search.querySelector('.cc-search__input');
    const dropdown = search.querySelector('.cc-search-suggestions');
    if (!input || !dropdown) return;

    let timer = null;
    let controller = null;
    let closeTimer = null;
    let requestId = 0;

    const openDropdown = () => {
      window.clearTimeout(closeTimer);
      dropdown.hidden = false;
      requestAnimationFrame(() => {
        dropdown.classList.add('is-open');
      });
    };

    const closeDropdown = ({ clear = true } = {}) => {
      dropdown.classList.remove('is-open');
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        if (!dropdown.classList.contains('is-open')) {
          dropdown.hidden = true;
          if (clear) {
            dropdown.innerHTML = '';
          }
        }
      }, CLOSE_ANIMATION_MS);
    };

    const setLoading = () => {
      openDropdown();
      dropdown.innerHTML =
        '<div class="cc-search-suggestions__loading">Buscando delícias...</div>';
    };

    const renderEmpty = () => {
      openDropdown();
      dropdown.innerHTML =
        '<div class="cc-search-suggestions__empty">Nenhum produto encontrado.</div>';
    };

    const renderResults = (items) => {
      if (!items.length) {
        renderEmpty();
        return;
      }

      openDropdown();
      dropdown.innerHTML = `
        <div class="cc-search-suggestions__header">Sugestões</div>
        ${items
          .map(
            (item) => `
            <a class="cc-search-suggestion" href="${escapeHtml(item.href)}">
              ${
                item.image
                  ? `<img class="cc-search-suggestion__image" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy">`
                  : '<div class="cc-search-suggestion__image" aria-hidden="true"></div>'
              }
              <div class="cc-search-suggestion__content">
                <p class="cc-search-suggestion__title">${escapeHtml(item.title)}</p>
                ${
                  item.price || item.comparePrice
                    ? `<div class="cc-search-suggestion__prices">
                        ${item.comparePrice ? `<span class="cc-search-suggestion__compare">${escapeHtml(item.comparePrice)}</span>` : ''}
                        ${item.price ? `<span class="cc-search-suggestion__price">${escapeHtml(item.price)}</span>` : ''}
                      </div>`
                    : ''
                }
              </div>
            </a>
          `
          )
          .join('')}
      `;
    };

    const parseItemsFromMarkup = (markup) => {
      const doc = new DOMParser().parseFromString(markup, 'text/html');
      const cards = Array.from(doc.querySelectorAll('.predictive-search-results__card'));

      return cards
        .map((card) => {
          const resourceCard = card.querySelector('.resource-card');
          const linkEl = card.querySelector('.resource-card__link');
          const titleEl = card.querySelector('.resource-card__title');
          const imageEl = card.querySelector('.resource-card__image');
          const priceEl = card.querySelector('.price');
          const compareEl = card.querySelector('.compare-at-price');

          const srcsetFirst = imageEl?.getAttribute('srcset')?.split(',')?.[0]?.trim()?.split(' ')?.[0] || '';
          const rawImage = imageEl?.getAttribute('src') || imageEl?.getAttribute('data-src') || srcsetFirst;
          const rawHref = linkEl?.getAttribute('href') || '';
          const resourceType = resourceCard?.dataset?.resourceType;

          return {
            href: normalizeHref(rawHref),
            title: cleanText(titleEl),
            image: normalizeImageUrl(rawImage),
            price: cleanText(priceEl),
            comparePrice: cleanText(compareEl),
            type: resourceType === 'collection' ? 'Colecao' : 'Produto',
          };
        })
        .filter((item) => item.title && item.href !== '#');
    };

    const fetchSuggestions = async (query) => {
      const q = query.trim();

      if (q.length < MIN_QUERY_LENGTH) {
        closeDropdown();
        return;
      }

      requestId += 1;
      const currentRequestId = requestId;

      if (controller) controller.abort();
      controller = new AbortController();

      setLoading();

      const params = new URLSearchParams();
      params.set('q', q);
      params.set('resources[limit]', '10');
      params.set('resources[type]', 'product,collection');
      params.set('resources[options][unavailable_products]', 'last');
      params.set('resources[options][fields]', 'title,body,product_type,variants.title,variants.sku,vendor');
      params.set('section_id', 'predictive-search');

      const endpoint = getPredictiveEndpoint();
      const requestUrl = `${endpoint}?${params.toString()}`;

      try {
        const response = await fetch(requestUrl, {
          signal: controller.signal,
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) {
          throw new Error(`Predictive search request failed with ${response.status}`);
        }

        const html = await response.text();
        if (currentRequestId !== requestId) return;

        const items = parseItemsFromMarkup(html);
        renderResults(items);
      } catch (error) {
        if (error.name === 'AbortError') return;
        renderEmpty();
      }
    };

    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        fetchSuggestions(input.value);
      }, DEBOUNCE_MS);
    });

    input.addEventListener('focus', () => {
      if (input.value.trim().length >= MIN_QUERY_LENGTH) {
        fetchSuggestions(input.value);
      }
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeDropdown();
        input.blur();
      }
    });

    search.addEventListener('submit', () => {
      closeDropdown();
    });

    search.addEventListener('click', (event) => {
      if (event.target.closest('.cc-search-suggestion')) {
        closeDropdown();
      }
    });

    document.addEventListener('click', (event) => {
      if (!search.contains(event.target)) {
        closeDropdown();
      }
    });
  };

  const initHeaderSearch = (scope = document) => {
    const searches = scope.querySelectorAll('[data-cc-header-search]');
    searches.forEach((search) => {
      if (search.dataset.ccSearchReady === 'true') return;
      search.dataset.ccSearchReady = 'true';
      createSearchController(search);
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    initHeaderSearch();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initHeaderSearch(event.target);
  });
})();
